import type { NetworkBody } from '@belvdr/shared'
import { isSensitiveName, MASKED, maskParams, nameWords } from './network-url.js'

/**
 * Longueur maximale d'un corps enregistré (cf. `MAX_NETWORK_BODY_LENGTH` de `@belvdr/shared`,
 * recopiée : le SDK n'importe que des types de ce paquet, qui embarque zod).
 */
const MAX_NETWORK_BODY_LENGTH = 10 * 1024

/** Type de contenu textuel (`text/*`, JSON, XML, formulaire, JavaScript, GraphQL…). */
export function isTextual(contentType: string | null | undefined): boolean {
  if (!contentType) return false
  const type = contentType.split(';')[0]!.trim().toLowerCase()
  return (
    type.startsWith('text/') ||
    /^application\/([\w.-]+\+)?(json|xml)$/.test(type) ||
    type === 'application/x-www-form-urlencoded' ||
    type === 'application/javascript' ||
    type === 'application/graphql'
  )
}

/**
 * Codes d'erreur des réponses (`code`, `errorCode`, `statusCode`…) : `code` rend un paramètre
 * d'URL sensible (code OAuth), mais dans un corps en erreur c'est l'information qu'on cherche.
 */
const ERROR_CODE_NAMES = new Set(['code', 'errorcode', 'statuscode'])

/** La clé `key` d'un corps JSON ou d'un formulaire porte-t-elle une valeur sensible ? */
function isSensitiveKey(key: string): boolean {
  if (ERROR_CODE_NAMES.has(nameWords(key).join(''))) return false
  return isSensitiveName(key)
}

/** Valeur JSON dont les valeurs des clés sensibles deviennent `[masked]`, à toute profondeur. */
function maskJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(maskJson)
  if (typeof value !== 'object' || value === null) return value
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key,
      isSensitiveKey(key) ? MASKED : maskJson(item),
    ]),
  )
}

/**
 * Paire `"clé": valeur` d'un texte JSON, la valeur étant un texte ou un scalaire (un objet ou un
 * tableau commence après les deux-points : ses propres clés sont examinées à leur tour).
 */
const JSON_PAIR = /"((?:[^"\\]|\\.)*)"(\s*:\s*)("(?:[^"\\]|\\.)*"|[^\s,{}[\]"]+)?/g

/** Texte JSON qui ne s'analyse pas (coupé, mal formé) : masquage des paires `"clé": valeur`. */
function maskJsonText(text: string): string {
  return text.replace(JSON_PAIR, (pair, key: string, colon: string, value?: string) =>
    value !== undefined && isSensitiveKey(key) ? `"${key}"${colon}"${MASKED}"` : pair,
  )
}

/** Corps de formulaire (`a=1&b=2`), sans espace, qui n'a pas l'air d'un JSON coupé. */
const FORM = /^(?![{[])[^\s=&]+=[^\s&]*(?:&[^\s&]*)*$/

/**
 * Texte d'un corps dont les valeurs des clés sensibles deviennent `[masked]` : JSON (à toute
 * profondeur, tableaux compris), formulaire, et à défaut les paires `"clé": valeur` d'un JSON
 * qui ne s'analyse pas (ou d'un texte qui en contient).
 */
function maskBody(text: string): string {
  try {
    const value: unknown = JSON.parse(text)
    if (typeof value === 'object' && value !== null) return JSON.stringify(maskJson(value))
  } catch {
    // Pas du JSON : formulaire, texte…
  }
  if (FORM.test(text)) return maskParams(text)
  return maskJsonText(text)
}

/** Corps limité à 10 Ko, sans couper un caractère en deux (paire de substitution UTF-16). */
function truncate(text: string): NetworkBody {
  if (text.length <= MAX_NETWORK_BODY_LENGTH) return { text }
  let end = MAX_NETWORK_BODY_LENGTH
  const last = text.charCodeAt(end - 1)
  if (last >= 0xd800 && last <= 0xdbff) end--
  return { text: text.slice(0, end), truncated: true }
}

/** Ce que reçoit `maskNetworkBody` avec le corps : à quelle requête en erreur il appartient. */
export type NetworkBodyContext = {
  /** Corps envoyé (`request`) ou reçu (`response`). */
  kind: 'request' | 'response'
  /** URL absolue de la requête, telle que demandée par la page (avant tout masquage). */
  url: string
  /** Méthode HTTP, en majuscules. */
  method: string
  /** Statut HTTP de la réponse (4xx / 5xx). */
  status: number
}

/**
 * Réécrit le corps (texte, tel qu'envoyé ou reçu) d'une requête en erreur avant son
 * enregistrement, ou renvoie `null` pour ne pas l'enregistrer. Le masquage automatique des clés
 * sensibles et la limite de 10 Ko s'appliquent ensuite au résultat.
 */
export type MaskNetworkBody = (body: string, context: NetworkBodyContext) => string | null

/**
 * Corps d'une requête en erreur à enregistrer, ou `null` pour ne pas l'enregistrer : exclu par
 * `maskBody` (ou s'il échoue). Dans tous les autres cas, il est masqué puis tronqué.
 */
export function bodyRecorder({ maskBody: hook }: { maskBody?: MaskNetworkBody | undefined }) {
  return (text: string, context: NetworkBodyContext): NetworkBody | null => {
    let rewritten: unknown = text
    if (hook) {
      try {
        rewritten = hook(text, context)
      } catch {
        // Hook en échec : rien n'est enregistré plutôt qu'un corps qu'il devait peut-être masquer.
        return null
      }
    }
    if (typeof rewritten !== 'string') return null
    // Masqué en entier avant d'être tronqué : un JSON coupé ne s'analyserait plus.
    return truncate(maskBody(rewritten))
  }
}
